import { useEffect, useRef } from 'react'
import { useResetOnChange } from './useResetOnChange'

export function useDebouncedValue<T>(
  value: T,
  delay: number,
  resetKey: string,
): T {
  // Re-seeded when the subject changes, so a new event never briefly shows the
  // previous event's debounced value.
  const [debounced, setDebounced] = useResetOnChange<T>(resetKey, () => value)

  // Read only from the effects below, never during render.
  const debouncing = useRef(false)
  useEffect(() => {
    debouncing.current = false
  }, [resetKey])

  useEffect(() => {
    // Pass through the first change immediately so a single version bump
    // (e.g. revisiting an event after admin seeded new sets) resolves
    // without delay. Only debounce rapid subsequent changes (e.g. during
    // active festival seeding that stamps contentUpdatedAt many times).
    if (!debouncing.current) {
      debouncing.current = true
      setDebounced(value)
    }

    const timer = setTimeout(() => {
      debouncing.current = false
      setDebounced(value)
    }, delay)
    return () => clearTimeout(timer)
  }, [value, delay, setDebounced])

  // Until a defined value has landed, hand back the raw one so there's no
  // one-render gap between "event loaded" and "version available".
  return debounced === undefined ? value : debounced
}
